import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/adminApi";

const FIELD = "w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-3 text-navy placeholder:text-ink-muted/60 transition-colors";
const LABEL = "font-mono text-[10px] uppercase tracking-[0.24em] text-ink";

export const BannerEditor = ({ onAuthError }) => {
  const [b, setB] = useState({ enabled: false, text: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/banner")
      .then((r) => setB({ ...r.data, end_date: r.data.end_date || "" }))
      .catch((e) => e?.response?.status === 401 && onAuthError());
  }, [onAuthError]);

  const save = async (e) => {
    e.preventDefault();
    if (b.enabled && !b.text.trim()) { toast.error("Add the banner text before turning it on."); return; }
    setSaving(true);
    try {
      await adminApi.put("/admin/banner", { ...b, end_date: b.end_date || null });
      toast.success(b.enabled ? "Banner is live on the site." : "Banner saved and hidden.");
    } catch (err) {
      if (err?.response?.status === 401) onAuthError(); else toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <section data-testid="admin-banner-editor" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Site notice</p>
      <h2 className="font-serif text-3xl text-navy">Holiday / hours banner</h2>
      <p className="text-ink-muted text-sm mt-3 max-w-lg">Shows a slim bar at the very top of every page. Set an end date and it hides itself automatically after that day.</p>

      <form onSubmit={save} className="mt-8 space-y-8" noValidate>
        <label className="flex items-center gap-4 cursor-pointer select-none">
          <button type="button" role="switch" aria-checked={b.enabled} data-testid="banner-enabled-toggle"
            onClick={() => setB((s) => ({ ...s, enabled: !s.enabled }))}
            className={`relative w-12 h-6 border transition-colors ${b.enabled ? "bg-navy border-navy" : "bg-cream border-[#C9BFA8]"}`}>
            <span className={`absolute top-0.5 h-[18px] w-[18px] transition-transform ${b.enabled ? "translate-x-6 bg-gold" : "translate-x-0.5 bg-[#C9BFA8]"}`} />
          </button>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-navy">{b.enabled ? "Banner on" : "Banner off"}</span>
        </label>

        <div>
          <label className={LABEL} htmlFor="banner-text">Banner text</label>
          <input id="banner-text" data-testid="banner-text-input" className={FIELD} maxLength={240} value={b.text}
            onChange={(e) => setB((s) => ({ ...s, text: e.target.value }))}
            placeholder="Closed Thursday, Nov 26 for Thanksgiving. Back Saturday." />
        </div>

        <div className="max-w-xs">
          <label className={LABEL} htmlFor="banner-end">Auto-hide after (optional)</label>
          <input id="banner-end" type="date" data-testid="banner-end-date-input" className={FIELD} value={b.end_date}
            onChange={(e) => setB((s) => ({ ...s, end_date: e.target.value }))} />
        </div>

        <button type="submit" disabled={saving} data-testid="banner-save-btn"
          className="bg-navy text-parchment px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] border border-gold/30 hover:-translate-y-0.5 transition-all disabled:opacity-60">
          {saving ? "Saving…" : "Save banner"}
        </button>
      </form>
    </section>
  );
};
